import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface DeleteAccountModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (password: string, reason?: string) => Promise<void>;
}

export default function DeleteAccountModal({ isOpen, onClose, onConfirm }: DeleteAccountModalProps) {
    const [step, setStep] = useState<1 | 2>(1);
    const [confirmed, setConfirmed] = useState(false);
    const [password, setPassword] = useState('');
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleClose = () => {
        setStep(1);
        setConfirmed(false);
        setPassword('');
        setReason('');
        setError('');
        setIsLoading(false);
        onClose();
    };

    const handleConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!password.trim()) {
            setError('Veuillez entrer votre mot de passe');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            await onConfirm(password, reason || undefined);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Une erreur est survenue');
            setIsLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                        className="absolute inset-0 bg-black/50 dark:bg-black/70"
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="relative bg-white dark:bg-mcdm-third-400 rounded-lg shadow-xl p-6 w-full max-w-md mx-4 z-10"
                    >
                        {step === 1 ? (
                            <>
                                <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
                                    Supprimer mon compte
                                </h3>
                                <div className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-200 space-y-3 mb-6">
                                    <p>Cette action entraînera :</p>
                                    <ul className="list-disc pl-5 space-y-1">
                                        <li>La désactivation immédiate de votre compte</li>
                                        <li>L'annulation de vos abonnements actifs</li>
                                        <li>L'anonymisation de vos données personnelles après 30 jours</li>
                                    </ul>
                                    <p>Vos publications publiques (dossiers) resteront accessibles mais seront anonymisées.</p>
                                    <p className="font-medium text-red-600 dark:text-red-400">
                                        Vous disposez de 30 jours pour contacter le support et réactiver votre compte.
                                    </p>
                                </div>

                                <label className="flex items-start gap-2 mb-6 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={confirmed}
                                        onChange={(e) => setConfirmed(e.target.checked)}
                                        className="mt-1 accent-red-500"
                                    />
                                    <span className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-200">
                                        Je comprends que cette action est irréversible après 30 jours
                                    </span>
                                </label>

                                <div className="flex gap-3 justify-end">
                                    <button
                                        onClick={handleClose}
                                        className="px-4 py-2 text-sm rounded-lg border border-gray-200 dark:border-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-200 hover:bg-gray-50 dark:hover:bg-mcdm-third-300 transition-colors"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        onClick={() => setStep(2)}
                                        disabled={!confirmed}
                                        className={`px-4 py-2 text-sm rounded-lg text-white transition-colors ${
                                            confirmed
                                                ? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
                                                : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                                        }`}
                                    >
                                        Continuer
                                    </button>
                                </div>
                            </>
                        ) : (
                            <form onSubmit={handleConfirm}>
                                <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
                                    Confirmation de suppression
                                </h3>
                                <p className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-200 mb-4">
                                    Entrez votre mot de passe pour confirmer la suppression de votre compte.
                                </p>

                                <div className="mb-4">
                                    <label className="block text-sm text-mcdm-third-500 dark:text-mcdm-primary-200 mb-1">
                                        Mot de passe
                                    </label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Votre mot de passe"
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-mcdm-third-300 rounded-lg text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 bg-white dark:bg-mcdm-third-300 focus:outline-none focus:ring-2 focus:ring-red-300"
                                        autoFocus
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm text-mcdm-third-500 dark:text-mcdm-primary-200 mb-1">
                                        Raison (optionnel)
                                    </label>
                                    <textarea
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="Dites-nous pourquoi vous partez..."
                                        rows={2}
                                        maxLength={500}
                                        className="w-full px-3 py-2 border border-gray-200 dark:border-mcdm-third-300 rounded-lg text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 bg-white dark:bg-mcdm-third-300 focus:outline-none focus:ring-2 focus:ring-red-300 resize-none"
                                    />
                                </div>

                                {error && (
                                    <p className="text-red-500 text-sm mb-4">{error}</p>
                                )}

                                <div className="flex gap-3 justify-end">
                                    <button
                                        type="button"
                                        onClick={() => { setStep(1); setError(''); setPassword(''); }}
                                        className="px-4 py-2 text-sm rounded-lg border border-gray-200 dark:border-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-200 hover:bg-gray-50 dark:hover:bg-mcdm-third-300 transition-colors"
                                    >
                                        Retour
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isLoading || !password.trim()}
                                        className={`px-4 py-2 text-sm rounded-lg text-white transition-colors ${
                                            isLoading || !password.trim()
                                                ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                                                : 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
                                        }`}
                                    >
                                        {isLoading ? 'Suppression...' : 'Supprimer définitivement'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
